/**
 * Workspace Entity
 * Represents an organization/tenant in the multi-tenant system
 */

/**
 * Supported grading scale types
 */
export type GradeScaleType = 'percentage' | 'venezuela' | 'mexico' | 'colombia';

/**
 * Grade scale configuration
 */
export interface GradeScale {
  /** Scale type identifier */
  type: GradeScaleType;
  /** Minimum value in the scale */
  min: number;
  /** Maximum value in the scale */
  max: number;
  /** Number of decimal places allowed */
  decimals: number;
  /** Display label for the scale */
  label: string;
}

/**
 * Predefined grade scale presets
 */
export const GRADE_SCALE_PRESETS: Record<GradeScaleType, GradeScale> = {
  percentage: {
    type: 'percentage',
    min: 0,
    max: 100,
    decimals: 1,
    label: '0-100 (Porcentaje)',
  },
  venezuela: {
    type: 'venezuela',
    min: 1,
    max: 20,
    decimals: 1,
    label: '1-20 (Venezuela)',
  },
  mexico: {
    type: 'mexico',
    min: 0,
    max: 10,
    decimals: 1,
    label: '0-10 (México)',
  },
  colombia: {
    type: 'colombia',
    min: 0,
    max: 5,
    decimals: 1,
    label: '0-5 (Colombia)',
  },
};

/**
 * Default passing thresholds by scale type (in native scale)
 */
export const DEFAULT_THRESHOLDS_BY_SCALE: Record<GradeScaleType, { passing: number; unlock: number }> = {
  percentage: { passing: 60, unlock: 70 },
  venezuela: { passing: 10, unlock: 14 },
  mexico: { passing: 6, unlock: 7 },
  colombia: { passing: 3, unlock: 3.5 },
};

/**
 * Academic grading rules configuration
 */
export interface GradingRules {
  /** Grade scale configuration */
  scale: GradeScale;
  /** Minimum grade to pass a course (in native scale) */
  passingGrade: number;
  /** Minimum grade to unlock next level (in native scale) */
  levelUnlockGrade: number;
}

/**
 * Default grading rules (percentage scale)
 */
export const DEFAULT_GRADING_RULES: GradingRules = {
  scale: GRADE_SCALE_PRESETS.percentage,
  passingGrade: 60,
  levelUnlockGrade: 70,
};

/**
 * Standard decimal precision for all grade calculations
 */
export const GRADE_DECIMAL_PRECISION = 2;

/**
 * Grade scale utility functions
 */
export const GradeScaleUtils = {
  /**
   * Convert a grade from native scale to percentage (0-100)
   * Uses simple division: grade/max * 100
   * e.g., 10/20 = 50%, 8/20 = 40%
   */
  toPercentage(grade: number, scale: GradeScale): number {
    return (grade / scale.max) * 100;
  },

  /**
   * Convert a percentage (0-100) to native scale
   * e.g., 50% on 1-20 scale = 10
   */
  fromPercentage(percentage: number, scale: GradeScale): number {
    return (percentage / 100) * scale.max;
  },

  /**
   * Format a grade for display with scale context
   */
  formatGrade(grade: number | null, scale: GradeScale): string {
    if (grade === null) return '--';
    const formatted = grade.toFixed(scale.decimals);
    return `${formatted} / ${scale.max}`;
  },

  /**
   * Format just the grade value (without max)
   */
  formatGradeValue(grade: number | null, scale: GradeScale): string {
    if (grade === null) return '--';
    return grade.toFixed(scale.decimals);
  },

  /**
   * Validate if a grade is within the scale range
   */
  isValidGrade(grade: number, scale: GradeScale): boolean {
    return grade >= scale.min && grade <= scale.max;
  },

  /**
   * Get validation error message for invalid grade
   */
  getValidationError(grade: number, scale: GradeScale): string | null {
    if (grade < scale.min) {
      return `El valor mínimo es ${scale.min}`;
    }
    if (grade > scale.max) {
      return `El valor máximo es ${scale.max}`;
    }
    return null;
  },

  /**
   * Get the scale preset by type
   */
  getPreset(type: GradeScaleType): GradeScale {
    return GRADE_SCALE_PRESETS[type];
  },

  /**
   * Get default thresholds for a scale type
   */
  getDefaultThresholds(type: GradeScaleType): { passing: number; unlock: number } {
    return DEFAULT_THRESHOLDS_BY_SCALE[type];
  },

  /**
   * Get letter grade from a grade value.
   * Converts to percentage first if a non-percentage scale is provided.
   */
  getLetterGrade(grade: number | null, scale?: GradeScale): string | null {
    if (grade === null) return null;
    const percentage = scale && scale.type !== 'percentage'
      ? GradeScaleUtils.toPercentage(grade, scale)
      : grade;
    if (percentage >= 90) return 'A';
    if (percentage >= 80) return 'B';
    if (percentage >= 70) return 'C';
    if (percentage >= 60) return 'D';
    return 'F';
  },

  /**
   * Round a grade value to the standard precision (2 decimal places)
   */
  roundGrade(value: number): number {
    const factor = Math.pow(10, GRADE_DECIMAL_PRECISION);
    return Math.round(value * factor) / factor;
  },
};

export interface WorkspaceSettings {
  timezone?: string;
  locale?: string;
  branding?: {
    primaryColor?: string;
    logoUrl?: string;
  };
  features?: {
    lmsEnabled?: boolean;
    notificationsEnabled?: boolean;
    statisticsEnabled?: boolean;
    allowLessonDownloads?: boolean;
  };
  /** Academic grading rules */
  gradingRules?: GradingRules;
  [key: string]: unknown;
}

export interface WorkspaceProps {
  id: string;
  name: string;
  slug: string;
  settings: WorkspaceSettings;
  createdAt: Date;
  updatedAt: Date;
}

export class Workspace {
  private readonly props: WorkspaceProps;

  constructor(props: WorkspaceProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get name(): string {
    return this.props.name;
  }

  get slug(): string {
    return this.props.slug;
  }

  get settings(): WorkspaceSettings {
    return this.props.settings;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get updatedAt(): Date {
    return this.props.updatedAt;
  }

  /**
   * Check if the workspace has a specific feature enabled
   */
  isFeatureEnabled(feature: keyof NonNullable<WorkspaceSettings['features']>): boolean {
    return this.props.settings.features?.[feature] ?? false;
  }

  /**
   * Get the workspace timezone
   */
  getTimezone(): string {
    return this.props.settings.timezone ?? 'UTC';
  }

  /**
   * Get the workspace locale
   */
  getLocale(): string {
    return this.props.settings.locale ?? 'es';
  }

  /**
   * Get the grading rules with defaults
   */
  getGradingRules(): GradingRules {
    const settingsRules = this.props.settings.gradingRules;
    if (!settingsRules) {
      return DEFAULT_GRADING_RULES;
    }
    return {
      scale: settingsRules.scale ?? DEFAULT_GRADING_RULES.scale,
      passingGrade: settingsRules.passingGrade ?? DEFAULT_GRADING_RULES.passingGrade,
      levelUnlockGrade: settingsRules.levelUnlockGrade ?? DEFAULT_GRADING_RULES.levelUnlockGrade,
    };
  }

  /**
   * Get the grade scale
   */
  getGradeScale(): GradeScale {
    return this.props.settings.gradingRules?.scale ?? DEFAULT_GRADING_RULES.scale;
  }

  /**
   * Get the minimum passing grade (in native scale)
   */
  getPassingGrade(): number {
    return this.props.settings.gradingRules?.passingGrade ?? DEFAULT_GRADING_RULES.passingGrade;
  }

  /**
   * Get the minimum grade to unlock next level (in native scale)
   */
  getLevelUnlockGrade(): number {
    return this.props.settings.gradingRules?.levelUnlockGrade ?? DEFAULT_GRADING_RULES.levelUnlockGrade;
  }

  /**
   * Check if a grade passes (in native scale)
   */
  isPassing(grade: number): boolean {
    return grade >= this.getPassingGrade();
  }

  /**
   * Check if a grade unlocks next level (in native scale)
   */
  canUnlockLevel(grade: number): boolean {
    return grade >= this.getLevelUnlockGrade();
  }

  /**
   * Check if lesson downloads are allowed for this workspace
   * Defaults to false for security (opt-in feature)
   */
  isLessonDownloadsAllowed(): boolean {
    return this.props.settings.features?.allowLessonDownloads ?? false;
  }

  /**
   * Check if the workspace was created recently (within the last 30 days)
   */
  isNew(): boolean {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return this.props.createdAt > thirtyDaysAgo;
  }

  /**
   * Generate the workspace URL path
   */
  getUrlPath(): string {
    return `/${this.props.slug}`;
  }

  toPlainObject(): WorkspaceProps {
    return { ...this.props };
  }
}
